<template>
  <div class="rangeChart">
    <el-row type="flex" justify="space-between" class="mr-l15 mr-r15">
      <el-button class="mr-t10" @click="moveTimeZone(true)"><i class="far fa-angle-left"></i></el-button>
      <svg  ref="rangeSvg" class="rangeSvgclass" width="96%" height="100%"></svg>
      <el-button class="mr-t10" @click="moveTimeZone(false)"><i class="far fa-angle-right"></i></el-button>
    </el-row>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import * as d3 from 'd3';
import moment from 'moment';
import { isNil, cloneDeep } from 'lodash';

export default {
  name: 'WLogKeywordTimeSlider',
  props: {
    timeOption: {
      type: Object,
    },
    active: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      dateformat: 'HH:mm',
      step: 1,
      xScale: null,
      rangeValues: null,
      handle: null,
      startTime: null,
      endTime: null,
    };
  },
  computed: mapGetters(['analysisDateParams']),
  watch: {
    analysisDateParams() {
      if (this.active) {
        this.timeOption.interval = this.getTimeInterval(this.analysisDateParams);
        this.$nextTick(() => {
          this.creatRangeChart();
          this.$emit('dragTimeRangeChart');
        });
      }
    },
    active: {
      handler(to) {
        if (to) {
          this.timeOption.interval = this.getTimeInterval(this.analysisDateParams);

          this.$nextTick(() => {
            this.creatRangeChart();
            this.$emit('dragTimeRangeChart');
          });
        } else {
          const rangeChart = d3.select('.rangeSvgclass');
          rangeChart.selectAll('*').remove();
        }
      },
    },
  },
  methods: {
    /**
     * 시간 범위 이벤트
     * @author leesy918
     * @param  {[type]} direction [description]
     * @return {[type]}           [description]
     */
    moveTimeZone(direction) {
      const timeInterval = (this.endTime - this.startTime);
      if (direction) {
        this.endTime = this.startTime;
        this.startTime = this.startTime - timeInterval;
        this.creatRangeChart();
      } else {
        this.startTime = this.endTime;
        this.endTime = this.endTime + timeInterval;
        this.creatRangeChart();
      }
    },
    /**
     * 시간 범위 차트 x축 표현 설정
     * @author leesy918
     * @param  {[type]} value [description]
     * @return {[type]}       [description]
     */
    getDateFomatter(value) {
      let format = cloneDeep(this.dateformat);
      if ((this.dateformat === 'HH:mm' && (value === this.startTime || value === this.endTime))) {
        format = 'MM-DD HH:mm';
      }
      return moment(new Date(value)).format(format);
    },

    /**
     * 시간 범위 차트 작성 함수
     * @author leesy918
     * @return {[type]} [description]
     */
    creatRangeChart() {
      if (isNil(this.endTime)) {
        const endDate = new Date(this.analysisDateParams.endDatetime);
        endDate.setMilliseconds(0);
        this.endTime = endDate.setSeconds(0);
      }
      if (isNil(this.startTime)) {
        const startDate = new Date(this.analysisDateParams.startDatetime);
        startDate.setMilliseconds(0);
        this.startTime = startDate.setSeconds(0);
      }
      const range = [this.startTime, this.endTime];
      const svg = d3.select('.rangeSvgclass');
      svg.select('*').remove();
      const margin = { left: 30, right: 30 };
      const svgWidth = svg.style('width');
      const width = Number(svgWidth.substring(0, svgWidth.length - 2));
      const height = 45;
      // 시간 범위 차트 추가
      const slider = svg.append('g')
        .classed('slider', true)
        .attr('transform', `translate(${margin.left}, ${height / 2})`);
      // 차트 작성 범위 설정


      // array useful for step sliders
      this.rangeValues = d3.range(range[0], range[1], this.timeOption.interval || 1).concat(range[1]);
      // 노드 사이 간격이 불규칙 적인 경우 설정된 interval을 이용해 재설정 해준다.
      if (this.rangeValues.length > 1 && (this.rangeValues[this.rangeValues.length - 1] - this.rangeValues[this.rangeValues.length - 2] < this.timeOption.interval)) {
        this.rangeValues[this.rangeValues.length - 1] = this.rangeValues[this.rangeValues.length - 2] + this.timeOption.interval;
        this.endTime = this.rangeValues[this.rangeValues.length - 1];
        range[1] = this.endTime;
      }

      this.xScale = d3.scaleLinear()
        .domain(range)
        .range([0, width - margin.left - margin.right])
        .clamp(true);

      const xAxis = d3.axisBottom(this.xScale).tickValues(this.rangeValues).tickFormat((d) => this.getDateFomatter(d));

      this.xScale.clamp(true);
      // 시간 범위 차트 드래그 이벤트 초기화
      const drag = d3.drag()
        .on('start.interrupt', () => {
          slider.interrupt();
        }).on('end', () => {
          this.dragged(d3.event.x);
          this.$emit('dragTimeRangeChart');
        });

      // 시간 범위 차트 메인 프레임
      const track = slider.append('line').attr('class', 'track')
        .attr('x1', this.xScale.range()[0])
        .attr('x2', this.xScale.range()[1]);

      // 시간 범위 차트 안쪽 트랙 설정
      d3.select(slider.node().appendChild(track.node().cloneNode())).attr('class', 'track-inset');

      slider.append('g').attr('class', 'ticks').attr('transform', 'translate(0, 4)')
        .call(xAxis);

      // 드래그 핸들러
      this.handle = slider.append('circle').classed('handle', true)
        .attr('r', 8);

      d3.select(slider.node().appendChild(track.node().cloneNode())).attr('class', 'track-overlay')
        .call(drag);
      slider.transition()
        .tween('drag', () => {
          this.dragged(this.xScale(this.timeOption.selectStep));
        });
    },
    /**
     * 시간 선택 차트 드래그 이벤트 핸들러
     * @author leesy918
     * @param  {[type]} value [description]
     * @return {[type]}       [description]
     */
    dragged(value) {
      const x = this.xScale.invert(value);
      let index = null;
      let midPoint;
      let cx;
      let xVal;
      if (this.step) {
        // step 값이 있을때 시간 범위의 midpoint 설정

        for (let i = 0; i < this.rangeValues.length - 1; i += 1) {
          if (x >= this.rangeValues[i] && x <= this.rangeValues[i + 1]) {
            index = i;
            break;
          }
        }

        midPoint = (this.rangeValues[index] + this.rangeValues[index + 1]) / 2;
        if (x < midPoint) {
          cx = this.xScale(this.rangeValues[index]);
          xVal = this.rangeValues[index];
        } else {
          cx = this.xScale(this.rangeValues[index + 1]);
          xVal = this.rangeValues[index + 1];
        }
      } else {
        // step이 0 또는 null 일때
        cx = this.xScale(x);
        xVal = x.toFixed(3);
      }
      // use xVal as drag value
      this.handle.attr('cx', cx);
      this.timeOption.selectStep = xVal;
    },
    /**
     * 과거 시간인지 현재 시간인지 비교한다
     * @author leesy918
     * @return {[type]} [description]
     */
    compareTime() {
      const date = new Date();
      const analysisDate = new Date(this.analysisDateParams.endDatetime);
      if (analysisDate.getTime() - date.getTime() < 0) {
        return true;
      }
      return false;
    },
    /**
     * interval 타입에 따른 포멧 설정
     * @author leesy918
     * @param  {[type]} params [description]
     * @return {[type]}        [description]
     */
    getTimeInterval(params) {
      let timeInterval = null;
      const statisticType = this.getStatisticType(params.startDatetime, params.endDatetime);
      const pastFlag = this.compareTime();
      const currentDate = new Date();
      const ms = 1000 * 60 * 60;
      const date = new Date(Math.round(currentDate.getTime() / ms) * ms);
      date.setSeconds(0);
      date.setMilliseconds(0);
      switch (statisticType) {
        case 'DAY':
          this.dateformat = 'HH:mm';
          date.setMinutes(0);
          if (pastFlag) {
            date.setDate(date.getDate() - 1);
          }
          this.timeOption.selectStep = date.getTime();
          timeInterval = 60 * 60;
          break;
        case 'DAY3':
          this.dateformat = 'HH:mm';
          date.setMinutes(0);
          if (pastFlag) {
            date.setDate(date.getDate() - 1);
          }
          this.timeOption.selectStep = date.getTime();
          timeInterval = 60 * 60 * 3;
          break;
        case 'WEEK':
          this.dateformat = 'MM-DD HH:mm';
          date.setMinutes(0);
          if (pastFlag) {
            date.setDate(date.getDate() - 7);
          }
          this.timeOption.selectStep = date.getTime();
          timeInterval = 12 * 60 * 60;
          break;
        case 'MONTH':
          this.dateformat = 'MM-DD';
          timeInterval = 24 * 60 * 60;
          date.setMinutes(0);
          if (pastFlag) {
            date.setMonth(date.getMonth() - 1);
          }
          this.timeOption.selectStep = date.getTime();
          break;
        case '2MONTH':
          this.dateformat = 'MM-DD';
          timeInterval = 24 * 60 * 60 * 2;
          date.setMinutes(0);
          if (pastFlag) {
            date.setMonth(date.getMonth() - 2);
          }
          this.timeOption.selectStep = date.getTime();
          break;
        case '3MONTH':
          this.dateformat = 'MM-DD';
          timeInterval = 24 * 60 * 60 * 3;
          date.setMinutes(0);
          if (pastFlag) {
            date.setMonth(date.getMonth() - 3);
          }
          this.timeOption.selectStep = date.getTime();
          break;
        case 'YEAR':
          this.dateformat = 'YYYY-MM';
          timeInterval = 31 * 24 * 60 * 60;
          date.setMinutes(0);
          if (pastFlag) {
            date.setFullYear(date.getFullYear() - 1);
          }
          this.timeOption.selectStep = date.getTime();
          break;
        case 'HOURS':
          this.dateformat = 'HH:mm';
          timeInterval = 60 * 5;
          this.timeOption.seleteStep = date.getTime();
          break;
        case 'MINUTES30':
          this.dateformat = 'HH:mm';
          timeInterval = 60 * 2;
          this.timeOption.selectStep = date.getTime();
          break;
        default:
          this.dateformat = 'HH:mm';
          timeInterval = 60;
          this.timeOption.selectStep = date.getTime();
          break;
      }
      this.startTime = null;
      this.endTime = null;
      return timeInterval * 1000;
    },
    /**
     * 시간 선택에 따른 interval 단위 설정
     * @author leesy918
     * @param  {[type]} startDateTime [description]
     * @param  {[type]} endDateTime   [description]
     * @return {[type]}               [description]
     */
    getStatisticType(startDateTime, endDateTime) {
      const gap = endDateTime - startDateTime - 1000;
      const MINUTES = 1000 * 60;
      const MINUTES30 = 1000 * 60 * 30;
      const HOURS = 1000 * 60 * 60;
      const DAY = 1000 * 60 * 60 * 24;
      const DAY3 = 1000 * 60 * 60 * 24 * 3;
      const DAY19 = 1000 * 60 * 60 * 24 * 19;
      const DAY30 = 1000 * 60 * 60 * 24 * 30;
      const DAY60 = 1000 * 60 * 60 * 24 * 60;
      const DAY90 = 1000 * 60 * 60 * 24 * 90;

      if (gap >= 0 && gap <= MINUTES) {
        return 'MINUTES';
      } else if (gap > MINUTES && gap <= MINUTES30) {
        return 'MINUTES30';
      } else if (gap > MINUTES30 && gap <= HOURS) {
        return 'HOURS';
      } else if (gap > HOURS && gap <= DAY) {
        return 'DAY';
      } else if (gap > DAY && gap <= DAY3) {
        return 'DAY3';
      } else if (gap > DAY && gap <= DAY19) {
        return 'WEEK';
      } else if (gap > DAY19 && gap <= DAY30) {
        return 'MONTH';
      } else if (gap > DAY30 && gap <= DAY60) {
        return '2MONTH';
      } else if (gap > DAY60 && gap <= DAY90) {
        return '3MONTH';
      } else if (gap > DAY90) {
        return 'YEAR';
      }
      return 'MINUTES';
    },
  },
};
</script>

<style>

</style>



// WEBPACK FOOTER //
// src/views/log-analysis/KeywordMap/LogKeywordTimeSlider.vue